import React from 'react'
import { AlertTriangle, CircleCheck } from 'lucide-react'

import { Skeleton, usePAWStore } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useFormat } from '~/components/hooks/use-format'
import { useTranslation } from '~/app/i18n/client'
import { PAWSectionType } from './paw-section-types'

type CampaignCardProps = {
  title: string
  memberIds: string[]
  notParticipatingMembers: string[]
  totals: Record<string, number>
  teamMembers: Record<string, any>[]
  members: Record<string, any>
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  title,
  memberIds,
  notParticipatingMembers,
  totals,
  teamMembers,
  members,
}: CampaignCardProps) => {
  const { formatNumber } = useFormat()

  const teamMembersCount = teamMembers?.length
  const memberCount = memberIds?.length

  const isComplete = memberCount === teamMembersCount

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-500">Participation</span>
        <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-gray-900">
          {memberCount}/{teamMembersCount}
          {isComplete && <CircleCheck className="h-5 w-5 text-green-600" />}
        </span>
      </div>

      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all ${
            isComplete ? 'bg-green-500' : 'bg-blue-500'
          }`}
          style={{
            width: `${(memberCount / teamMembersCount) * 100}%`,
          }}
        />
      </div>

      <div className="my-4 h-px bg-gray-100" />

      {Object.keys(totals ?? {})?.map((key) => (
        <div key={key} className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Team Points</span>
          <span className="text-sm font-semibold tabular-nums text-gray-900">
            {formatNumber(totals?.[key])}
          </span>
        </div>
      ))}

      {notParticipatingMembers && notParticipatingMembers.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            Not yet participating:
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {notParticipatingMembers
              ?.filter((key) =>
                Boolean(members?.[key]?.firstName && members?.[key]?.lastName),
              )
              ?.map((key: string) => (
                <span
                  key={key}
                  className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
                >
                  {members?.[key]?.firstName + ' ' + members?.[key]?.lastName}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const Engagement: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()
  const { date } = usePAWStore((state) => state)
  const { t: translate } = useTranslation()

  const { data: campaigns, isLoading: interactionsLoading } =
    api.pawDashboards.getTeamInteractions.useQuery({
      ...date,
      fetchPayloads: true,
      ...(content?.metadata?.params ?? {}),
    })

  return (
    <section id={content?.sectionId} data-testid={content?.sectionId}>
      <h2 className="mb-6 text-xl font-semibold">
        {content?.title ?? 'Program Engagement'}
      </h2>
      <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
        {interactionsLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-[150px] w-full" />
          ))
        ) : Object.keys(campaigns?.data ?? {})?.length ? (
          Object.keys(campaigns?.data ?? {}).map((key) => (
            <div key={key}>
              <CampaignCard
                title={key}
                memberIds={campaigns?.data?.[key]?.memberIds}
                notParticipatingMembers={
                  campaigns?.data?.[key]?.notParticipatingMembers
                }
                totals={campaigns?.data?.[key]?.totals}
                teamMembers={campaigns?.teamMembers ?? []}
                members={campaigns?.members ?? {}}
              />
            </div>
          ))
        ) : (
          <div className="text-sm text-slate-800">
            {translate('pawDashboards.noDataFound')}
          </div>
        )}
      </div>
    </section>
  )
}
